import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Edit, Trash2, Eye, Bus } from 'lucide-react';
import { Bus as BusType, BusType as BusTypeInterface } from '../../types';

interface BusTableProps {
  buses: BusType[];
  busTypes: BusTypeInterface[];
  onEdit: (bus: BusType) => void;
  onDelete: (busId: string) => void;
  onView: (bus: BusType) => void;
}

export const BusTable: React.FC<BusTableProps> = ({
  buses,
  busTypes,
  onEdit,
  onDelete,
  onView,
}) => {
  const getBusTypeName = (busTypeId: string) => {
    const busType = busTypes.find(type => type.id === busTypeId);
    return busType?.name || 'Unknown';
  };

  const getStatusBadge = (bus: BusType) => {
    if (!bus.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }

    if (!bus.isInService) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Maintenance
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        In Service
      </span>
    );
  };

  const getFuelTypeBadge = (fuelType: string) => {
    const colors = {
      electric: 'bg-purple-100 text-purple-800',
      diesel: 'bg-gray-100 text-gray-800',
      hybrid: 'bg-teal-100 text-teal-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[fuelType as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {fuelType ? fuelType.charAt(0).toUpperCase() + fuelType.slice(1) : 'Unknown'}
      </span>
    );
  };

  if (buses.length === 0) {
    return (
      <Card className="p-8 text-center">
        <div className="text-secondary-500">
          <Bus className="h-12 w-12 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No buses found</h3>
          <p>Get started by adding your first bus to the fleet.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="bg-secondary-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Bus Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Type & Operator
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Capacity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Fuel Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Amenities
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {buses.map((bus) => (
              <tr key={bus.id} className="hover:bg-secondary-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-secondary-900">
                      {bus.busNumber}
                    </div>
                    <div className="text-sm text-secondary-500">
                      {bus.registrationNumber}
                    </div>
                    <div className="text-xs text-secondary-400">
                      Year: {bus.yearOfManufacture}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-secondary-900">
                    {getBusTypeName(bus.busType?.id)}
                  </div>
                  <div className="text-sm text-secondary-500">
                    {bus.operatorName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-secondary-900">
                    {bus.availableSeats} / {bus.totalSeats} seats
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{
                        width: `${(bus.availableSeats / bus.totalSeats) * 100}%`,
                      }}
                    ></div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getFuelTypeBadge(bus.fuelType)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(bus)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {bus.amenities?.slice(0, 3).map((amenity) => amenity && (
                      <span
                        key={amenity.id}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {amenity.name}
                      </span>
                    ))}
                    {bus.amenities?.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-secondary-100 text-secondary-600">
                        +{bus.amenities.length - 3} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onView(bus)}
                      className="p-2"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(bus)}
                      className="p-2"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(bus.id)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
